import React, { useContext } from 'react';
import { ResultsContext } from '../contexts/ResultsContext';
import { ThemeContext } from '../contexts/ThemeContext';
import './HistoricalComparison.css';

/**
 * HistoricalComparison component displays a comparison of ranks between current and historical data
 * 
 * @param {Object} props - Component props
 * @param {string} props.username - Username to compare
 * @param {Object} props.historicalData - Historical results data
 * @param {boolean} props.isMe - Whether the username is the current user
 */
function HistoricalComparison({ username, historicalData, isMe }) {
  const { results, loading } = useContext(ResultsContext);
  const { theme } = useContext(ThemeContext);

  if (loading || !results || !results.games || !username) {
    return null;
  }

  // Calculate comparisons
  const comparisons = [];
  
  if (historicalData && historicalData.games) {
    results.games.forEach(currentGame => {
      const currentLeaderboard = currentGame.leaderboard || [];
      const currentUserEntry = currentLeaderboard.find(
        entry => entry.userName.toLowerCase() === username.toLowerCase()
      );
      
      const historicalGame = historicalData.games.find(g => g.title === currentGame.title);
      const historicalLeaderboard = historicalGame?.leaderboard || [];
      const historicalUserEntry = historicalLeaderboard.find(
        entry => entry.userName.toLowerCase() === username.toLowerCase()
      );

      const currentRank = currentUserEntry ? currentUserEntry.rank : null;
      const historicalRank = historicalUserEntry ? historicalUserEntry.rank : null;

      if (currentRank !== null || historicalRank !== null) {
        let change = 0;
        let changeIndicator = '';
        let changeClass = '';
        let message = null;

        if (currentRank !== null && historicalRank !== null) {
          change = historicalRank - currentRank; // positive means rank improved (e.g. 10 -> 5 = +5)
          if (change > 0) {
            changeIndicator = '↑';
            changeClass = 'rank-improved';
          } else if (change < 0) {
            changeIndicator = '↓';
            changeClass = 'rank-worse';
            
            // Generate CTA message for rank drops
            const subject = isMe ? 'You' : username;
            const action = isMe ? 'Go set a new high score' : 'They need a new high score';
            message = `${subject} dropped a rank! ${action} on ${currentGame.title} to take it back!`;
          } else {
            // No movement, skip this game
            return;
          }
        } else if (currentRank !== null) {
           // New ranking (previously unranked)
           changeIndicator = '↑';
           changeClass = 'rank-improved';
        } else if (historicalRank !== null) {
           // Lost ranking (now unranked)
           changeIndicator = '↓';
           changeClass = 'rank-worse';
           const subject = isMe ? 'You' : username;
           const action = isMe ? 'Go set a new high score' : 'They need a new high score';
           message = `${subject} is no longer ranked on this game! ${action} to get back on the board!`;
        }

        comparisons.push({
          gameTitle: currentGame.title,
          currentRank: currentRank !== null ? `#${currentRank}` : 'Unranked',
          historicalRank: historicalRank !== null ? `#${historicalRank}` : 'Unranked',
          changeIndicator,
          changeClass,
          message
        });
      }
    });
  }

  return (
    <div className={`historical-comparison-card ${theme}`}>
      <div className="card-header">
        <div className="friend-name-container">
          {results?.users?.[username]?.avatar_path && (
            <div 
              className="friend-avatar-circle" 
              style={{ backgroundColor: results.users[username].background_color_hex, width: '28px', height: '28px' }}
            >
              <img 
                src={results.users[username].avatar_path} 
                alt={username} 
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          <h3 style={{ color: results?.users?.[username]?.background_color_hex, margin: 0 }}>
            {isMe ? 'My Movement' : `${username}'s Movement`}
          </h3>
        </div>
      </div>

      {!historicalData && (
        <div className="no-comparisons">Select a snapshot to see movement.</div>
      )}

      {historicalData && comparisons.length > 0 && (
        <div className="movement-list">
          {comparisons.map(comp => (
            <div key={comp.gameTitle} className="movement-item">
              <div className="movement-row">
                <div className="game-title-simplified">{comp.gameTitle}</div>
                <div className={`rank-movement ${comp.changeClass}`}>
                  {comp.changeIndicator} {comp.currentRank}
                  <span className="past-rank-context"> (was {comp.historicalRank})</span>
                </div>
              </div>
              
              {comp.message && (
                <div className="cta-message-box">
                  <p>{comp.message}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {historicalData && comparisons.length === 0 && (
        <div className="no-comparisons">No ranking data found to compare for {username}.</div>
      )}
    </div>
  );
}

export default HistoricalComparison;
